import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiClient, { DashboardStats } from "../../services/api"; // Corrected: Assuming apiClient is default export and import DashboardStats
import type { RootState } from "../../app/store";
// import { AuthUser } from '../auth/authSlice';

// Interfaces (ensure these match backend responses and frontend needs)
export interface FetchRequisitionsParams {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    store_ids?: number[];
    category_id?: number | null;
    area_coach_id?: number | null;
    showJobCardRequiredOnly?: boolean;
}

export interface RejectRequisitionPayload {
    comment: string;
}

export interface MinimalStore {
    storenumber?: string;
    id: number;
    name: string;
}

export interface MinimalUser {
    id: number;
    name: string;
}

export interface Approver {
    id?: number;
    user_id: number;
    user: MinimalUser;
    status_id: number;
    comment?: string;
    comments?: string;
}

export interface AssetItem {
    id?: number;
    asset: string;
    descriptions?: DescriptionItem[];
}

export interface DescriptionItem {
    id?: number;
    description: string;
}

export interface RepairItem {
    id?: number;
    description: string;
}

export interface EquipmentRequestItem {
    id?: number;
    equipment_smallware_id?: number;
    equipment?: {
        id: number;
        description: string;
        category?: string;
        subcategory?: string;
        serial_number?: string;
    };
    notes?: string;
}

export interface ProcessedEquipmentItem {
    asset: string;
    equipment: {
        category: string;
        subcategory: string;
        serial_number: string;
    };
    descriptions: string[];
}

export interface ProcessedAssetDescriptionPair {
    asset: string;
    descriptions: string[];
}

export interface HistoryEntry {
    id?: number;
    user_id: number;
    user?: MinimalUser;
    action: string;
    comment?: string;
    created_at: string;
}

export interface PictureItem {
    id?: number;
    attachment: string;
    comment?: string;
}

// Define the structure for a reply item based on your Blade template
export interface ReplyItem {
    id?: number; // Or string, if IDs are not numbers
    user: MinimalUser; // Assuming it has a nested user object with name
    content: string;
    created_at: string; // Assuming it's a string timestamp
    // Add any other properties that a reply object might have
}

export interface RequisitionListItem {
    id: number;
    date: string;
    status_id: number;
    submitted: boolean | number;
    internal?: boolean | number;
    stores: MinimalStore[];
    category?: { id: number; name: string };
    subcategory?: { id: number; name: string };
    user: MinimalUser;
    supplier?: { id: number; name: string };
    technician?: MinimalUser;
    approvers?: Approver[];
    updated_at: string;
    order?: {
        id: number;
        order_number?: string;
        reference_number?: string;
        jobCards?: JobCard[];
        jobcards?: JobCard[];
        job_cards?: JobCard[];
    };
    status?: { id: number; name: string; code?: string };
    quotation?: {
        id: number;
        amount: number;
        vat?: boolean | number;
        attachment?: string;
    };
    jobCard?: JobCard;
    jobCards?: JobCard[];
}

export interface RequisitionDetail extends RequisitionListItem {
    supplier?: { id: number; name: string };
    technician?: MinimalUser;
    assets?: AssetItem[];
    repairs?: RepairItem[];
    equipmentRequests?: EquipmentRequestItem[];
    histories?: HistoryEntry[];
    pictures?: PictureItem[];
    internal: number;
    is_repair?: number; // Add is_repair property
    replies?: ReplyItem[]; // Add replies here
    quotation?: {
        id: number;
        amount: number;
        date?: string;
        quotation_number?: string;
        vat?: boolean | number;
        attachment?: string;
    }; // Add quotation here
    equipmentItems?: ProcessedEquipmentItem[]; // Add processed equipment data
    assetDescriptionPairs?: ProcessedAssetDescriptionPair[]; // Add processed asset pairs
    jobCard?: JobCard;
    jobCards?: JobCard[];
}

export interface JobCard {
    id?: number;
    job_number?: string;
    attachment?: string;
    created_at?: string;
}

export interface PaginatedRequisitionsResponse {
    current_page: number;
    data: RequisitionListItem[];
    first_page_url: string;
    from: number | null;
    last_page: number;
    last_page_url: string;
    links: any[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number | null;
    total: number;
}

export interface AddHistoryPayload {
    requisitionId: string;
    comment: string;
    action?: string;
}

// New payload interface for adding a comment (reply)
export interface AddCommentPayload {
    requisitionId: string;
    content: string;
}

interface RequisitionState {
    list: RequisitionListItem[];
    selectedRequisition: RequisitionDetail | null;
    pagination: PaginatedRequisitionsResponse | null;
    isLoadingList: boolean;
    isLoadingDetails: boolean;
    isUpdating: boolean;
    error: string | null | unknown;
    filters: FetchRequisitionsParams;
    dashboardStats: DashboardStats | null;
    isLoadingDashboardStats: boolean;
    dashboardError: string | null;
}

const initialFilters: FetchRequisitionsParams = {
    status: "pending_my_approval",
    page: 1,
    per_page: 15,
    search: "",
    date_from: "",
    date_to: "",
    showJobCardRequiredOnly: false,
};

const initialState: RequisitionState = {
    list: [],
    selectedRequisition: null,
    pagination: null,
    isLoadingList: false,
    isLoadingDetails: false,
    isUpdating: false,
    error: null,
    filters: initialFilters,
    dashboardStats: null,
    isLoadingDashboardStats: false,
    dashboardError: null,
};

// Async Thunks
export const fetchRequisitionsList = createAsyncThunk<
    PaginatedRequisitionsResponse, // Return type
    FetchRequisitionsParams | undefined, // Argument type (undefined for no params explicitly passed)
    { state: RootState; rejectValue: string } // ThunkApi config
>("requisitions/fetchList", async (params, { getState, rejectWithValue }) => {
    try {
        const currentFilters = getState().requisitions.filters;
        const mergedParams = { ...currentFilters, ...(params || {}) }; // Ensure params is an object

        // Remove client-side filter parameters from API request
        const apiParams = { ...mergedParams };
        delete apiParams.area_coach_id; // Area coach filtering is done client-side
        delete apiParams.showJobCardRequiredOnly; // Job card filtering is done client-side

        // Optimize page size based on filters
        if (
            mergedParams.area_coach_id ||
            mergedParams.showJobCardRequiredOnly
        ) {
            // Request a larger page size for client-side filtering
            apiParams.per_page = 30; // Reduced from 50 for faster initial load
        } else {
            // Standard page size for normal requests
            apiParams.per_page = 20; // Reduced from default for faster loading
        }

        const response = await apiClient.get("/approver/requisitions", {
            params: apiParams,
        });
        return response.data as PaginatedRequisitionsResponse;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to fetch requisitions"
        );
    }
});

export const fetchRequisitionDetails = createAsyncThunk<
    RequisitionDetail,
    string, // Argument is id (string)
    { rejectValue: string }
>("requisitions/fetchDetails", async (id, { rejectWithValue }) => {
    try {
        const response = await apiClient.get(`/approver/requisitions/${id}`);
        return response.data as RequisitionDetail;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ||
                "Failed to fetch requisition details"
        );
    }
});

export const approveRequisitionAction = createAsyncThunk<
    RequisitionDetail,
    string, // Argument is id (string)
    { rejectValue: string }
>("requisitions/approve", async (id, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            `/approver/requisitions/${id}/approve`
        );
        return response.data as RequisitionDetail;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to approve requisition"
        );
    }
});

export const rejectRequisitionAction = createAsyncThunk<
    RequisitionDetail,
    { id: string; data: RejectRequisitionPayload }, // Argument type
    { rejectValue: string }
>("requisitions/reject", async ({ id, data }, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            `/approver/requisitions/${id}/reject`,
            data
        );
        return response.data as RequisitionDetail;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to reject requisition"
        );
    }
});

export const addRequisitionHistoryEntry = createAsyncThunk<
    HistoryEntry, // Return type: the newly created history entry
    AddHistoryPayload, // Argument type
    { state: RootState; rejectValue: string } // ThunkApi config
>("requisitions/addHistory", async (payload, { getState, rejectWithValue }) => {
    try {
        // This thunk might not be directly called from UI anymore if comments handle history.
        // Or, it can be used for specific non-comment history entries.
        const response = await apiClient.post(
            `/approver/requisitions/${payload.requisitionId}/history`,
            payload
        );
        return response.data as HistoryEntry;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to add history entry"
        );
    }
});

// Add bulk approve and bulk reject thunks
export const bulkApproveRequisitions = createAsyncThunk<
    { message: string; updatedIds: number[] },
    number[], // Array of requisition IDs
    { rejectValue: string }
>("requisitions/bulkApprove", async (ids, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            "/approver/requisitions/bulk-approve",
            { ids }
        );
        return response.data as { message: string; updatedIds: number[] };
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ||
                "Failed to bulk approve requisitions"
        );
    }
});

export const bulkRejectRequisitions = createAsyncThunk<
    { message: string; updatedIds: number[] },
    { ids: number[]; comment: string }, // Array of requisition IDs and rejection comment
    { rejectValue: string }
>("requisitions/bulkReject", async ({ ids, comment }, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            "/approver/requisitions/bulk-reject",
            { ids, comment }
        );
        return response.data as { message: string; updatedIds: number[] };
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ||
                "Failed to bulk reject requisitions"
        );
    }
});

// Thunk to add a comment (reply) to a requisition
export const addRequisitionCommentAction = createAsyncThunk<
    ReplyItem, // Return type: the newly created reply item
    AddCommentPayload, // Argument type
    { rejectValue: string }
>("requisitions/addComment", async (payload, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(
            `/approver/requisitions/${payload.requisitionId}/comments`,
            {
                content: payload.content,
            }
        );
        return response.data.reply as ReplyItem; // Assuming backend returns { reply: ReplyItem, history?: HistoryEntry }
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message || "Failed to add comment (thunk)"
        );
    }
});

// New Thunk for fetching dashboard stats
export const fetchDashboardStatsAction = createAsyncThunk<
    DashboardStats,
    void, // No arguments needed
    { rejectValue: string }
>("requisitions/fetchDashboardStats", async (_, { rejectWithValue }) => {
    try {
        const response = await apiClient.get("/approver/dashboard-stats");
        return response.data as DashboardStats;
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ||
                "Failed to fetch dashboard statistics"
        );
    }
});

export const requisitionSlice = createSlice({
    name: "requisitions",
    initialState,
    reducers: {
        clearRequisitionError: (state: RequisitionState) => {
            state.error = null;
        },
        clearSelectedRequisition: (state: RequisitionState) => {
            state.selectedRequisition = null;
        },
        setFilters: (
            state: RequisitionState,
            action: PayloadAction<Partial<FetchRequisitionsParams>>
        ) => {
            state.filters = { ...state.filters, ...action.payload, page: 1 }; // Reset page to 1 on filter change
            // If pagination exists and a page is part of the payload, update current_page
            if (state.pagination && typeof action.payload.page === "number") {
                state.pagination.current_page = action.payload.page;
            }
        },
        updateRequisitionFilters: (
            state: RequisitionState,
            action: PayloadAction<Partial<FetchRequisitionsParams>>
        ) => {
            state.filters = { ...state.filters, ...action.payload, page: 1 }; // Reset page to 1 on filter change
            state.pagination = null; // Clear pagination as new filter will fetch new data
        },
        resetRequisitionFilters: (state: RequisitionState) => {
            state.filters = initialFilters;
            state.pagination = null;
        },
        addOptimisticReply: (
            state: RequisitionState,
            action: PayloadAction<ReplyItem>
        ) => {
            if (
                state.selectedRequisition &&
                state.selectedRequisition.replies
            ) {
                state.selectedRequisition.replies.unshift(action.payload); // Add to the beginning for newest first
            } else if (state.selectedRequisition) {
                state.selectedRequisition.replies = [action.payload];
            }
        },
        removeOptimisticReply: (
            state: RequisitionState,
            action: PayloadAction<number | string>
        ) => {
            // Assuming reply ID is number or string
            if (
                state.selectedRequisition &&
                state.selectedRequisition.replies
            ) {
                state.selectedRequisition.replies =
                    state.selectedRequisition.replies.filter(
                        (reply) => reply.id !== action.payload
                    );
            }
        },
        updateOptimisticReply: (
            state: RequisitionState,
            action: PayloadAction<ReplyItem>
        ) => {
            if (
                state.selectedRequisition &&
                state.selectedRequisition.replies
            ) {
                const index = state.selectedRequisition.replies.findIndex(
                    (reply) => reply.id === action.payload.id
                );
                if (index !== -1) {
                    state.selectedRequisition.replies[index] = action.payload;
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                fetchRequisitionsList.pending,
                (state: RequisitionState) => {
                    state.isLoadingList = true;
                    state.error = null;
                }
            )
            .addCase(
                fetchRequisitionsList.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<PaginatedRequisitionsResponse>
                ) => {
                    state.isLoadingList = false;
                    // If it's the first page, replace the list. Otherwise, append.
                    if (action.payload.current_page === 1) {
                        state.list = action.payload.data;
                    } else {
                        // Prevent duplicates if a page is somehow fetched multiple times
                        const newItems = action.payload.data.filter(
                            (newItem) =>
                                !state.list.find(
                                    (existingItem) =>
                                        existingItem.id === newItem.id
                                )
                        );
                        state.list = [...state.list, ...newItems];
                    }
                    state.pagination = action.payload;
                }
            )
            .addCase(
                fetchRequisitionsList.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isLoadingList = false;
                    state.error =
                        action.payload || "Failed to load requisitions";
                }
            )
            .addCase(
                fetchRequisitionDetails.pending,
                (state: RequisitionState) => {
                    state.isLoadingDetails = true;
                    state.error = null;
                }
            )
            .addCase(
                fetchRequisitionDetails.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<RequisitionDetail>
                ) => {
                    state.isLoadingDetails = false;
                    state.selectedRequisition = action.payload;
                }
            )
            .addCase(
                fetchRequisitionDetails.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isLoadingDetails = false;
                    state.error =
                        action.payload || "Failed to load requisition details";
                }
            )
            .addCase(
                approveRequisitionAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                approveRequisitionAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<RequisitionDetail>
                ) => {
                    state.isUpdating = false;
                    state.selectedRequisition = action.payload; // Update selected with new data
                    // Optionally update list item if present
                    const index = state.list.findIndex(
                        (req) => req.id === action.payload.id
                    );
                    if (index !== -1) {
                        state.list[index] = {
                            ...state.list[index],
                            ...action.payload,
                        };
                    }
                }
            )
            .addCase(
                approveRequisitionAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error =
                        action.payload || "Failed to approve requisition";
                }
            )
            .addCase(
                rejectRequisitionAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                rejectRequisitionAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<RequisitionDetail>
                ) => {
                    state.isUpdating = false;
                    state.selectedRequisition = action.payload; // Update selected with new data
                    // Optionally update list item if present
                    const index = state.list.findIndex(
                        (req) => req.id === action.payload.id
                    );
                    if (index !== -1) {
                        state.list[index] = {
                            ...state.list[index],
                            ...action.payload,
                        };
                    }
                }
            )
            .addCase(
                rejectRequisitionAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error =
                        action.payload || "Failed to reject requisition";
                }
            )
            .addCase(
                addRequisitionHistoryEntry.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                }
            )
            .addCase(
                addRequisitionHistoryEntry.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<HistoryEntry>
                ) => {
                    state.isUpdating = false;
                    if (
                        state.selectedRequisition &&
                        state.selectedRequisition.histories
                    ) {
                        state.selectedRequisition.histories.unshift(
                            action.payload
                        );
                    } else if (state.selectedRequisition) {
                        state.selectedRequisition.histories = [action.payload];
                    }
                }
            )
            .addCase(
                addRequisitionHistoryEntry.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload ?? "Failed to add comment";
                }
            )
            // Bulk approve cases
            .addCase(
                bulkApproveRequisitions.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                bulkApproveRequisitions.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<{
                        message: string;
                        updatedIds: number[];
                    }>
                ) => {
                    state.isUpdating = false;
                    // Update requisitions in the list that were bulk approved
                    if (state.pagination && state.pagination.data) {
                        state.pagination.data = state.pagination.data.map(
                            (req) => {
                                if (
                                    action.payload.updatedIds.includes(req.id)
                                ) {
                                    return { ...req, status_id: 2 }; // Set status to approved
                                }
                                return req;
                            }
                        );
                    }
                }
            )
            .addCase(
                bulkApproveRequisitions.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error =
                        action.payload ?? "Failed to bulk approve requisitions";
                }
            )
            // Bulk reject cases
            .addCase(
                bulkRejectRequisitions.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true;
                    state.error = null;
                }
            )
            .addCase(
                bulkRejectRequisitions.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<{
                        message: string;
                        updatedIds: number[];
                    }>
                ) => {
                    state.isUpdating = false;
                    // Update requisitions in the list that were bulk rejected
                    if (state.pagination && state.pagination.data) {
                        state.pagination.data = state.pagination.data.map(
                            (req) => {
                                if (
                                    action.payload.updatedIds.includes(req.id)
                                ) {
                                    return { ...req, status_id: 3 }; // Set status to rejected
                                }
                                return req;
                            }
                        );
                    }
                }
            )
            .addCase(
                bulkRejectRequisitions.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error =
                        action.payload ?? "Failed to bulk reject requisitions";
                }
            )
            .addCase(
                addRequisitionCommentAction.pending,
                (state: RequisitionState) => {
                    state.isUpdating = true; // or a specific isSubmittingComment state
                    state.error = null;
                }
            )
            .addCase(
                addRequisitionCommentAction.fulfilled,
                (state: RequisitionState, action: PayloadAction<ReplyItem>) => {
                    state.isUpdating = false;
                    if (state.selectedRequisition) {
                        // Prepend new comment to existing replies for immediate UI update
                        const newReply = action.payload;
                        state.selectedRequisition.replies = [
                            newReply,
                            ...(state.selectedRequisition.replies || []),
                        ];

                        // Also, add to history if the backend structures the comment as a history entry as well
                        // This depends on your API response for addRequisitionCommentAction
                        // For now, let's assume the comment is primarily a "reply"
                    }
                }
            )
            .addCase(
                addRequisitionCommentAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isUpdating = false;
                    state.error = action.payload || "Failed to add comment";
                }
            )
            // Add cases for fetchDashboardStatsAction
            .addCase(
                fetchDashboardStatsAction.pending,
                (state: RequisitionState) => {
                    state.isLoadingDashboardStats = true;
                    state.dashboardError = null;
                }
            )
            .addCase(
                fetchDashboardStatsAction.fulfilled,
                (
                    state: RequisitionState,
                    action: PayloadAction<DashboardStats>
                ) => {
                    state.isLoadingDashboardStats = false;
                    state.dashboardStats = action.payload;
                }
            )
            .addCase(
                fetchDashboardStatsAction.rejected,
                (
                    state: RequisitionState,
                    action: PayloadAction<string | undefined>
                ) => {
                    state.isLoadingDashboardStats = false;
                    state.dashboardError =
                        action.payload || "Failed to fetch dashboard stats";
                }
            );
    },
});

export const {
    clearSelectedRequisition,
    clearRequisitionError,
    setFilters,
    updateRequisitionFilters,
    resetRequisitionFilters,
    addOptimisticReply,
    removeOptimisticReply,
    updateOptimisticReply,
} = requisitionSlice.actions;

export default requisitionSlice.reducer;
